import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import moment from 'moment';
import { FilterMapping } from '../../../../ui-components/lib/filter-dropdown/filter-mapping.enum';
import { ToasterService } from '../../../../ui-components/services/toast/toast.service';
import { SearchEnum } from '../../enums/search.enum';
import { CreatedRequest } from '../../models/created-by-me.model';
import { ApiResponse } from '../../models/api-response.model';
import { AllParticipatedRequest } from '../../models/all-participated.model';
import { Insight } from '../../models/insight.model';
import { ENDPOINTS } from '../../constants/endpoints.constants';
import { StepTask } from '../../models/step-task.model';

@Injectable({
  providedIn: 'root',
})
export class MyInboxService {
  private readonly http = inject(HttpClient);
  private readonly langHeader = 'Accept-Language';

  private addFiltersToParams(
    params: HttpParams,
    filters?: Record<string, string | string[]>,
    useCurrentAssignees = false
  ): HttpParams {
    if (!filters || Object.keys(filters).length === 0) return params;

    // Parse resourceType JSON into separate filter params
    const processedFilters = this.expandResourceTypeFilter(
      filters,
      useCurrentAssignees
    );

    Object.entries(processedFilters).forEach(([key, value]) => {
      if (!this.hasValidValue(value)) return;

      if (key === SearchEnum.keyword || key.includes('keyword')) return;

      const isNonIndexedArrayFilter = key.endsWith('][]');

      const isArrayFilter =
        !isNonIndexedArrayFilter &&
        (key.includes('][ids][') ||
          key === FilterMapping.WORKFLOW_TYPES ||
          key === FilterMapping.WORKFLOW_TYPES_FLOWS ||
          key === FilterMapping.PROJECT_IDS ||
          key === FilterMapping.HAS_FLOW_PROJECT_IDS ||
          key === FilterMapping.ASSIGNEES_IDS);

      if (isNonIndexedArrayFilter) {
        const items = this.normalizeToArray(value);
        items.forEach(item => {
          if (item?.trim()) {
            params = params.append(`filters[${key}`, item.trim());
          }
        });
      } else if (isArrayFilter) {
        const items = this.normalizeToArray(value);
        items.forEach((item, index) => {
          if (item?.trim()) {
            params = params.append(`filters[${key}${index}]`, item.trim());
          }
        });
      } else if (typeof value === 'string') {
        const formattedValue = this.formatFilterValue(key, value.trim());
        const filterKey = key.endsWith(']')
          ? `filters[${key}`
          : `filters[${key}]`;
        params = params.append(filterKey, formattedValue);
      }
    });

    return params;
  }

  private expandResourceTypeFilter(
    filters: Record<string, string | string[]>,
    useCurrentAssignees = false
  ): Record<string, string | string[]> {
    const resourceTypeValue = filters['resourceType'];
    if (!resourceTypeValue || typeof resourceTypeValue !== 'string') {
      return filters;
    }

    const expanded = { ...filters };
    delete expanded['resourceType'];

    const typeKey = useCurrentAssignees
      ? FilterMapping.CURRENT_ASSIGNEES_TYPE
      : FilterMapping.ASSIGNEES_TYPE;
    const idsKey = useCurrentAssignees
      ? FilterMapping.CURRENT_ASSIGNEES_IDS
      : FilterMapping.ASSIGNEES_IDS;

    const parsed = JSON.parse(resourceTypeValue);
    if (parsed.resourceType) {
      expanded[typeKey] = parsed.resourceType;
    }
    if (parsed.assigneeId && parsed.assigneeId.length > 0) {
      expanded[idsKey] = Array.isArray(parsed.assigneeId)
        ? parsed.assigneeId.map(String)
        : [String(parsed.assigneeId)];
    }

    return expanded;
  }

  private hasValidValue(value: string | string[] | null | undefined): boolean {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    return typeof value === 'string' && value.trim().length > 0;
  }

  private normalizeToArray(value: string | string[]): string[] {
    if (Array.isArray(value)) return value;
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  private formatFilterValue(key: string, value: string): string {
    const isDateFilter =
      key.includes(FilterMapping.CREATED_BEFORE) ||
      key.includes(FilterMapping.CREATED_AFTER) ||
      key.includes(FilterMapping.CREATED_BEFORE_FLOWS) ||
      key.includes(FilterMapping.CREATED_AFTER_FLOWS) ||
      key === FilterMapping.CREATED_BEFORE_REQUESTS ||
      key === FilterMapping.CREATED_AFTER_REQUESTS ||
      key === FilterMapping.COMPLETED_BEFORE_REQUESTS ||
      key === FilterMapping.COMPLETED_AFTER_REQUESTS;

    if (!isDateFilter || !moment(value).isValid()) {
      return value;
    }

    const parsed = moment(value);
    const [year, month, day] = [parsed.year(), parsed.month(), parsed.date()];
    const isAfter =
      key.includes(FilterMapping.CREATED_AFTER) ||
      key.includes(FilterMapping.CREATED_AFTER_FLOWS) ||
      key === FilterMapping.CREATED_AFTER_REQUESTS ||
      key === FilterMapping.COMPLETED_AFTER_REQUESTS;
    const [hour, minute, second] = isAfter ? [0, 0, 0] : [23, 59, 59];

    return moment
      .utc([year, month, day, hour, minute, second])
      .format('YYYY-MM-DD HH:mm:ss');
  }

  getStepsAssignToMe(
    page: number,
    per_page: number,
    filter: string,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    additionalFilters?: Record<string, string | string[]>,
    options?: { url?: string }
  ): Observable<ApiResponse<StepTask>> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);

    if (filter === 'sla_exceeded') {
      const currentDate = moment().utc().format('DD-MM-YYYY HH:mm:ss');
      params = params.append('filters[expired_at_before]', currentDate);
    }
    if (filter === 'on_hold') {
      params = params.append('filters[state]', 'on_hold');
    }
    if (filter === 'in_progress') {
      params = params.append('filters[state]', 'in_progress');
    }
    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }

    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters);
    }

    const url = options?.url ?? ENDPOINTS.Application.Inbox.Steps;
    return this.http.get<ApiResponse<StepTask>>(url, {
      params,
    });
  }

  getFlowsCreatedByMe(
    page: number,
    per_page: number,
    filter: string,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    additionalFilters?: Record<string, string | string[]>,
    options?: { url?: string }
  ): Observable<ApiResponse<CreatedRequest>> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);
    if (filter && filter !== 'all') {
      params = params.append('filters[state]', filter);
    }
    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }
    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }
    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters, true);
    }

    const url = options?.url ?? ENDPOINTS.Application.Inbox.Flows;
    return this.http.get<ApiResponse<CreatedRequest>>(url, {
      params,
    });
  }

  getAllParticipated(
    page: number,
    per_page: number,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    additionalFilters?: Record<string, string | string[]>
  ): Observable<ApiResponse<AllParticipatedRequest>> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);

    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }
    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }
    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters, true);
    }

    const url = ENDPOINTS.Application.Inbox.AllParticipated;
    return this.http.get<ApiResponse<AllParticipatedRequest>>(url, { params });
  }

  getTabsInsight(options?: { url?: string }): Observable<any> {
    const url = options?.url ?? ENDPOINTS.Application.Inbox.Insights;
    return this.http.get(url);
  }

  getFlowsCreatedByMeInsights(options?: {
    url?: string;
  }): Observable<{ data: Insight[] }> {
    const url = options?.url ?? ENDPOINTS.Application.Inbox.FlowInsights;
    return this.http.get<{ data: Insight[] }>(url);
  }

  exportCreatedByMe(form: any, options?: { url?: string }) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let headers = new HttpHeaders({});

    let params = new HttpParams();
    if (form.filters && form.filters !== 'all') {
      params = params.append('filters[state]', form.filters);
    }
    if (form.language) {
      headers = headers.append(this.langHeader, form.language);
    }
    if (form.type) {
      params = params.append('type', form.type);
    }
    params = params.append('timezone', timezone);

    const url = options?.url ?? ENDPOINTS.Export.createdByMe;
    return this.http.get(url, {
      params,
      headers,
      responseType: 'blob',
      observe: 'response',
    });
  }

  exportAssignedToMe(form: any, options?: { url?: string }) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let params = new HttpParams();
    let headers = new HttpHeaders({});

    if (form.filters === 'sla_exceeded') {
      const currentDate = moment().utc().format('DD-MM-YYYY HH:mm:ss');
      params = params.append('filters[expired_at_before]', currentDate);
    }

    if (form.filters === 'on_hold') {
      params = params.append('filters[state]', 'on_hold');
    }

    if (form.filters === 'in_progress') {
      params = params.append('filters[state]', 'in_progress');
    }

    if (form.language) {
      headers = headers.append(this.langHeader, form.language);
    }

    if (form.type) {
      params = params.append('type', form.type);
    }
    params = params.append('timezone', timezone);

    const url = options?.url ?? ENDPOINTS.Export.assignedToMe;
    return this.http.get(url, {
      params,
      headers,
      responseType: 'blob',
      observe: 'response',
    });
  }

  exportAllParticipated(form: any) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let headers = new HttpHeaders({});

    let params = new HttpParams();
    if (form.filters && form.filters !== 'all') {
      params = params.append('filters[state]', form.filters);
    }

    if (form.language) {
      headers = headers.append(this.langHeader, form.language);
    }

    if (form.type) {
      params = params.append('type', form.type);
    }
    params = params.append('timezone', timezone);

    return this.http.get(`${ENDPOINTS.Export.allParticipated}`, {
      params,
      headers,
      responseType: 'blob',
      observe: 'response',
    });
  }

  exportCollaboratedTasks(form: any) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let headers = new HttpHeaders({});

    let params = new HttpParams();
    if (form.filters && form.filters !== 'all') {
      params = params.append('filters[state]', form.filters);
    }
    if (form.language) {
      headers = headers.append(this.langHeader, form.language);
    }
    if (form.type) {
      params = params.append('type', form.type);
    }
    params = params.append('timezone', timezone);

    return this.http.get(`${ENDPOINTS.Export.collaboratedTasks}`, {
      params,
      headers,  
      responseType: 'blob',
      observe: 'response',
    });
  }

  getAllParticipatedInsight(): Observable<any> {
    const url = ENDPOINTS.Application.Inbox.AllParticipatedInsights;
    return this.http.get(url);
  }

  archiveRequest(
    workflowId: number,
    options?: { url?: string }
  ): Observable<any> {
    const url = (options?.url ?? ENDPOINTS.Application.Flow.Archive).replace(
      '{flowId}',
      workflowId.toString()
    );
    return this.http.delete(url, {});
  }

  restoreRequest(
    workflowId: number,
    options?: { url?: string }
  ): Observable<any> {
    const url = (options?.url ?? ENDPOINTS.Application.Flow.Restore).replace(
      '{flowId}',
      workflowId.toString()
    );
    return this.http.put(url, {});
  }

  getFilteredArchive(
    page: number,
    per_page: number,
    filter: string,
    sort: { key: string; value: 'asc' | 'desc' },
    projectId = 0,
    filterValue = 1,
    search?: string,
    additionalFilters?: Record<string, string | string[]>,
    modelType = 'project',
    options?: { url?: string }
  ): Observable<any> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);
    if (filter) {
      params = params.append(`filters[${filter}]`, filterValue);
    }
    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }
    if (projectId) {
      params = params.append('filters[model][type]', modelType);
      params = params.append('filters[model][id]', projectId);
    }
    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }
    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters);
    }

    const url = options?.url ?? ENDPOINTS.Application.Flow.Archived;
    return this.http.get(url, {
      params,
    });
  }

  getAssignedToMeInsights(options?: { url?: string }) {
    const url = options?.url ?? ENDPOINTS.Application.Inbox.StepInsights;
    return this.http.get<{ data: Insight[] }>(url);
  }

  getCollaboratedTasks(
    page: number,
    per_page: number,
    filter: string,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    additionalFilters?: Record<string, string | string[]>
  ): Observable<ApiResponse<CreatedRequest>> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);

    if (filter && filter !== 'all') {
      params = params.append('filters[state]', filter);
    }

    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }
    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }
    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters, true);
    }

    const url = ENDPOINTS.Application.Inbox.Collaborated;
    return this.http.get<ApiResponse<CreatedRequest>>(url, { params });
  }

  getCollaboratedTasksInsight(): Observable<any> {
    const url = ENDPOINTS.Application.Inbox.CollaboratedInsights;
    return this.http.get(url);
  }

  handleResponseError(
    error: { error: { message: string } },
    isLoading: WritableSignal<boolean>,
    toasterService: ToasterService
  ) {
    toasterService.add(error.error.message, '', '', 'error', true, true);
    isLoading.set(false);
  }

  getStepsContributedTasks(
    page: number,
    per_page: number,
    filter: string,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    additionalFilters?: Record<string, string | string[]>
  ): Observable<ApiResponse<StepTask>> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);

    if (filter === 'sla_exceeded') {
      const currentDate = moment().utc().format('DD-MM-YYYY HH:mm:ss');
      params = params.append('filters[expired_at_before]', currentDate);
    }
    if (filter === 'on_hold' || filter === 'in_progress') {
      params = params.append('filters[state]', filter);
    }

    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }

    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters);
    }

    const url = ENDPOINTS.Application.Inbox.Contributed;

    return this.http.get<ApiResponse<StepTask>>(url, {
      params,
    });
  }

  getContributedTasksInsight(): Observable<ApiResponse<Insight>> {
    const url = ENDPOINTS.Application.Inbox.ContributedInsights;
    return this.http.get<ApiResponse<Insight>>(url);
  }

  getStepsDelegatedToMe(
    page: number,
    per_page: number,
    filter: string,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    additionalFilters?: Record<string, string | string[]>
  ): Observable<ApiResponse<StepTask>> {
    return this.getStepsAssignToMe(
      page,
      per_page,
      filter,
      sort,
      search,
      additionalFilters,
      { url: ENDPOINTS.Application.Inbox.Delegated }
    );
  }

  getDelegatedToMeInsights() {
    return this.getAssignedToMeInsights({
      url: ENDPOINTS.Application.Inbox.DelegatedInsights,
    });
  }

  exportDelegatedToMe(form: Record<string, unknown>) {
    return this.exportAssignedToMe(form, {
      url: ENDPOINTS.Export.delegatedToMe,
    });
  }
}
